import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Closet, CLOSET_ORDER } from '../components/Closet';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { fetchClosets, type Closet as ClosetData } from '../lib/queries';
import {
  fetchFeedCycle,
  currentCycleStart,
  nextReset,
  type FeedCycleRule,
} from '../lib/feed-cycle';
import { Container, Heading, Stack, Text } from '../components/ui';
import './Feed.css';

const RESET_FMT = new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric' });

export default function Feed() {
  const [closets, setClosets] = useState<ClosetData[]>([]);
  const [cycleRule, setCycleRule] = useState<FeedCycleRule>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        // The reset cadence drives the cycle floor: only this cycle's posts
        // front their closets. fetchFeedCycle never throws (falls back to off).
        const rule = await fetchFeedCycle(ctrl.signal);
        const cycleStart = currentCycleStart(rule);
        const data = await fetchClosets(ctrl.signal, cycleStart ? cycleStart.toISOString() : null);
        if (ctrl.signal.aborted) return;
        setCycleRule(rule);
        setClosets(data);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[feed] fetchClosets failed', err);
        setError(err?.message ?? 'Something broke loading the closet.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Order the wall by the team's curated sequence (by drawer slug); any drawer
  // not in that list falls to the end, keeping sort_order among the rest.
  const ordered = useMemo(() => {
    const rank = (slug: string) => {
      const i = CLOSET_ORDER.indexOf(slug);
      return i === -1 ? CLOSET_ORDER.length : i;
    };
    return [...closets].sort(
      (a, b) => rank(a.drawer.slug) - rank(b.drawer.slug)
        || a.drawer.sort_order - b.drawer.sort_order,
    );
  }, [closets]);

  const cycleActive = !!cycleRule;
  const reset = nextReset(cycleRule);

  return (
    <div data-ds className="feed">
      {/* halftone dot-field is now site-wide (global.css body) */}
      <header className="feed__header">
        <Container className="feed__head">
          <Stack gap="tight">
            <p className="kicker">The Closets</p>
            <Heading level="h1" className="feed__headline">Coming up in the closets</Heading>
            <Text role="lede" muted className="feed__intro">
              The doors pop open throughout the month. You'll get an email when the
              next one opens, or{' '}
              <Link to="/calendar" className="feed__intro-link">check the calendar</Link>{' '}
              anytime.{cycleActive && reset ? ` Automatically reset ${RESET_FMT.format(reset)}.` : ''}
            </Text>
          </Stack>
          <DotRule className="feed__rule" />
        </Container>
      </header>

      <Container className="feed__body">
        {loading ? (
          <div className="feed__wall" aria-busy="true" aria-label="Loading the closet">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="feed__closet-skel">
                <Skeleton variant="card" aspectRatio={1180 / 1330} />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState eyebrow="Error" headline="We couldn't open the closet." body={error} />
        ) : closets.length === 0 ? (
          <EmptyState
            eyebrow="Nothing here yet"
            headline="The room is quiet, for now."
            body="The first pieces are publishing this week."
          />
        ) : (
          <>
            <div className="feed__wall">
              {ordered.map((c) => (
                <Closet key={c.drawer.id} drawer={c.drawer} post={c.post} />
              ))}
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
